import React, { useState, useCallback, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { NameSearchResponseData } from 'services/BackendApi/types/NameSearchResponse';
import { SvgIcon } from 'ui/SvgIcon';
import CircleXIcon from 'ui/Icons/tabler/circle-x.component.svg';
import LoaderIcon from 'ui/Icons/tabler/loader.component.svg';

export interface IApiCategoryDropdownProps {
  nameSearchResults: NameSearchResponseData | undefined;
  value: string;
  onChange: (text: string) => void;
  isLoading?: boolean;
  hasError?: boolean;
  placeholder?: string;
}

interface IOptionGroup {
  id: string;
  label: string;
  options: string[];
}

const buildOptionGroups = (nameSearchResults: NameSearchResponseData | undefined): IOptionGroup[] => {
  if (!nameSearchResults) return [];
  const groups: IOptionGroup[] = [];
  if (nameSearchResults.countries.length > 0) {
    groups.push({ id: 'destinations', label: 'Destinations', options: nameSearchResults.countries.map(c => c.name) });
  }
  if (nameSearchResults.regions.length > 0) {
    groups.push({ id: 'regions', label: 'Regions', options: nameSearchResults.regions.map(r => r.name) });
  }
  if (nameSearchResults.hotels.length > 0) {
    groups.push({ id: 'hotels', label: 'Hotels', options: nameSearchResults.hotels.map(h => h.name) });
  }
  return groups;
};

export const ApiCategoryDropdown: React.FC<IApiCategoryDropdownProps> = ({
  nameSearchResults,
  value,
  onChange,
  isLoading = false,
  hasError = false,
  placeholder = 'Select ...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const optionGroups = buildOptionGroups(nameSearchResults);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  const handleOptionSelect = useCallback(
    (name: string) => {
      onChange(name);
      setIsOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange('');
      inputRef.current?.focus();
    },
    [onChange]
  );

  return (
    <div ref={dropdownRef} className="relative w-full">
      <div
        className={classnames('relative flex w-full h-[39px] border border-solid items-center px-2', {
          'border-green-prime': isOpen,
          'border-gray-40': !isOpen,
          'bg-white': !hasError,
          'border-red-95 bg-red-25': hasError,
        })}
        onClick={() => { inputRef.current?.focus(); setIsOpen(true); onChange(value); }}
      >
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => { setIsOpen(true); onChange(value); }}
          placeholder={placeholder}
          className="flex-1 outline-none bg-transparent text-sm font-pt-sans normal-case"
          data-lpignore="true"
          autoComplete="off"
        />

        {value && (
          <SvgIcon
            IconComponent={CircleXIcon}
            onClick={handleClear}
            className="w-5 h-5 fill-brown-100 hover:fill-brown-120 cursor-pointer flex-shrink-0"
          />
        )}
      </div>

      {isOpen && (isLoading || optionGroups.length > 0) && (
        <div className="absolute top-full left-0 z-30 mt-1 bg-white-hard border border-solid border-gray-40 rounded shadow-lg max-h-[300px] overflow-y-auto min-w-full pr-[15px] normal-case">
          {isLoading ? (
            <div className="flex justify-center items-center py-4">
              <SvgIcon
                IconComponent={LoaderIcon}
                className="w-[18px] h-[18px] fill-transparent stroke-gray-80 stroke-2 animate-spin"
              />
            </div>
          ) : (
            <div className="p-2">
              {optionGroups.map(group => (
                <div key={group.id} className="mb-4 last:mb-0">
                  <div className="font-pt-sans text-[15px] font-bold text-black mb-[10px]">{group.label}</div>
                  <div className="flex flex-wrap gap-2">
                    {group.options.map(name => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => handleOptionSelect(name)}
                        className="bg-white font-pt-sans text-[13px] text-black p-[5px] rounded border border-solid border-gray-20 cursor-pointer transition-colors whitespace-nowrap"
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
